/**
 * Dashboard Page
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchProjects, updateProject, deleteProject } from '@/store/projectSlice';
import { fetchTasks, fetchRecentTasks, updateTask, updateSubtask } from '@/store/taskSlice';
import { ProjectCard } from '@/components/ProjectCard';
import { TaskCard } from '@/components/TaskCard';
import { BarChart3, CheckCircle, Clock, AlertCircle, Check, X } from 'lucide-react';
import { userAPI } from '@/api/userAPI';
import { shareAPI } from '@/api/shareAPI';
import { taskAPI } from '@/api/taskAPI';
import { commentAPI } from '@/api/commentAPI';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { deleteComment, formatCommentsForActivity, updateComment } from '@/utils/commentUtils';
import { CommentActivity, Comment as ProjectComment } from '@/types/comments';
import { TaskModal } from '@/components/TaskModal/TaskModal';
import SubtaskModal from '@/components/SubtaskModal/SubtaskModal';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import type { Project, Subtask, Task } from '@/types';
import toast from 'react-hot-toast';
import Share from '@/components/Share';
import { Loader } from '@/components/Loader';
import { subtaskAPI } from '@/api/subtaskAPI';
import { ensureHtml, toPlainText } from '@/utils/richText';
import {mastersAPI, Priority, WorkflowStatus } from '@/api/mastersApi';

export const Dashboard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { projects, loading: projectsLoading, currentProject } = useAppSelector((state) => state.projects);
  const { tasks, recentTasks, loading: tasksLoading } = useAppSelector((state) => state.tasks);
  const { user, isAdmin } = useAppSelector((state) => state.auth);

  // Task Modal States
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [comments, setComments] = useState<ProjectComment[]>([]);
  const [activity, setActivity] = useState<CommentActivity[]>([]);
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [activeSubtask, setActiveSubtask] = useState<Subtask | null>(null);
  const [isSubtaskModalOpen, setIsSubtaskModalOpen] = useState(false);
  const [parentTaskForSubtask, setParentTaskForSubtask] = useState<any>(null);

  // Fallback recent tasks for non-admin users
  const [fallbackRecentTasks, setFallbackRecentTasks] = useState<any[]>([]);

  // Share popover state
  const [isShareOpen, setIsShareOpen] = useState(false);
  const shareAnchorRef = useRef<HTMLButtonElement | null>(null);
  const shareRef = useRef<HTMLDivElement | null>(null);
  const [_, setShareLeft] = useState<number>(0);
  const [_1, setShareTop] = useState<number>(0);
  const [__2, setShareWidth] = useState<number>(420);
  const [shareTo, setShareTo] = useState('');
  const [shareMsg, setShareMsg] = useState('');
  const [_3, setDropdownOpen] = useState(false);
  const [___4, setResults] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [_5, setResultsLoading] = useState(false);
  const [recipients, setRecipients] = useState<Array<{ id?: string; email: string; name?: string }>>([]);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  
  // (projectViewerStatus is now a useMemo derived from project.my_role —
  // no separate state or per-project API calls needed.)
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [assignedTasks, setAssignedTasks] = useState<Task[]>([]);
  const [____6, setTaskStatsLoading] = useState(false);

  // Edit project state
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editName, setEditName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete project state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(fetchProjects({ is_active: true, organization_id:user?.organization_id }));
    // dispatch(fetchTasks({}));
    // dispatch(fetchRecentTasks({ limit: 6 }));

 // Fetch master data
    (async () => {
      try {
        const [prios, statuses] = await Promise.all([
          mastersAPI.getPriorities(),
          mastersAPI.getWorkflowStatuses()
        ]);
        setPriorities(prios.filter(p => p.is_active !== false).sort((a, b) => a.order - b.order));
        setWorkflowStatuses(statuses || []);
      } catch { }
    })();
  }, [dispatch]);
  useEffect(() => {
    if (!projects.length) return;
  
    const projectIds = projects
      .filter((p: any) => p.is_active !== false)
      .map((p: any) => p.id)
      .filter(Boolean);
  
    if (projectIds.length === 0) return;
  
    dispatch(fetchTasks({ project_ids: projectIds }));
    dispatch(fetchRecentTasks({ limit: 6, project_ids: projectIds }));
  }, [dispatch, projects]);
  const projectMap = useMemo(() => {
    return projects.reduce((acc, project) => {
      if (project?.id) {
        acc[project.id] = project as Project;
      }
      return acc;
    }, {} as Record<string, Project>);
  }, [projects]);

  // Stable derived values — memoised so downstream effects only re-run when the
  // actual set of IDs changes, not on every Redux array-reference change.
  const projectIds = useMemo(() =>
    projects
      .filter((p: any) => p.is_active !== false)
      .map((p: any) => p.id)
      .filter(Boolean),
    [projects]
  );
  const projectIdsKey = projectIds.join(',');

  // Fallback: If recent tasks is empty for non-admin users, fetch tasks from their associated projects
  // useEffect(() => {
  //   const loadFallbackTasks = async () => {
  //     // Only apply fallback for non-admin users when recent tasks is empty and projects are loaded
  //     if (!isAdmin && recentTasks.length === 0 && user?.id && !tasksLoading && !projectsLoading && projects.length > 0) {
  //       try {
  //         // Get project IDs from projects the user has access to
  //         const projectIds = projects
  //           .filter((p: any) => p.is_active !== false)
  //           .map((p: any) => p.id);

  //         if (projectIds.length === 0) {
  //           setFallbackRecentTasks([]);
  //           return;
  //         }

  //         // Fetch tasks from all user's associated projects in parallel
  //         const projectIdsToFetch = projectIds.slice(0, 10); // Limit to first 10 projects to avoid too many API calls

  //         const taskPromises = projectIdsToFetch.map((projectId: string) =>
  //           taskAPI.getTasks({
  //             project_id: projectId,
  //             limit: 20 // Get more tasks per project, then sort and limit overall
  //           }).catch((error) => {
  //             console.error(`Failed to fetch tasks for project ${projectId}:`, error);
  //             return []; // Return empty array on error to continue with other projects
  //           })
  //         );

  //         const subtaskPromises = projectIdsToFetch.map((projectId: string) =>
  //           taskAPI.getAllSubtasks({
  //             project_id: projectId,
  //             limit: 20 // Get more tasks per project, then sort and limit overall
  //           }).catch((error) => {
  //             console.error(`Failed to fetch subtasks for project ${projectId}:`, error);
  //             return []; // Return empty array on error to continue with other projects
  //           })
  //         );

  //         // Fetch all tasks in parallel - await each array of promises separately
  //         const taskResults = await Promise.allSettled(taskPromises);
  //         const subtaskResults = await Promise.allSettled(subtaskPromises);

  //         // Flatten the results: each promise resolves to an array of tasks/subtasks
  //         const tasksData: any[] = [];
  //         taskResults.forEach((result) => {
  //           if (result.status === 'fulfilled' && Array.isArray(result.value)) {
  //             tasksData.push(...result.value);
  //           }
  //         });

  //         const subtasksData: any[] = [];
  //         subtaskResults.forEach((result) => {
  //           if (result.status === 'fulfilled' && Array.isArray(result.value)) {
  //             subtasksData.push(...result.value);
  //           }
  //         });

  //         // Convert subtasks to task-like format for display
  //         const subtasksAsTasks: (Task | Subtask & { isSubtask?: boolean })[] = subtasksData.map(subtask => {
  //           // Find the parent task to get project_id and other shared fields
  //           const parentTask = tasks.find(t => t.id === subtask.task_id);
  //           return {
  //             ...subtask,
  //             // Map subtask fields to task-like structure
  //             project_id: parentTask?.project_id || '',
  //             workflow_status_id: subtask.workflow_status_id,
  //             priority_id: subtask.priority_id,
  //             parent_task_id: subtask.task_id,
  //             reporter_id: '',
  //             display_number: parentTask?.display_number ?? subtask.id?.substring(0, 6),
  //             status: (subtask.status as any) || 'todo',
  //             priority: (subtask.priority as any) || 'medium',
  //             isSubtask: true, // Flag to identify subtasks
  //           } as any;
  //         });

  //         // Combine tasks and subtasks
  //         const allTasks = [...tasksData, ...subtasksAsTasks].map(item => {
  //           const project = projectMap[(item as any).project_id];
  //           return {
  //             ...item,
  //             project,
  //             projectKey: project?.key || ''
  //           };
  //         })

  //         // Sort by updated_at descending (most recent first) and filter
  //         const sortedTasks = allTasks
  //           .filter((task: any) => task.is_active !== false && task.is_archived !== true)
  //           .sort((a: any, b: any) => {
  //             const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
  //             const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
  //             return dateB - dateA;
  //           })
  //           .slice(0, 6);

  //         setFallbackRecentTasks(sortedTasks);
  //       } catch (error) {
  //         console.error('Failed to load fallback recent tasks:', error);
  //         setFallbackRecentTasks([]);
  //       }
  //     } else {
  //       setFallbackRecentTasks([]);
  //     }
  //   };

  //   loadFallbackTasks();
  // }, [recentTasks, isAdmin, user?.id, tasksLoading, projectsLoading, projects]);
  useEffect(() => {
    const loadFallbackTasks = async () => {
      // When real recent tasks exist, clear the fallback without creating a new []
      // reference if it is already empty (prevents spurious viewer-status re-runs).
      if (isAdmin || recentTasks.length > 0 || !user?.id || tasksLoading || projectsLoading || !projectIds.length) {
        if (recentTasks.length > 0) {
          setFallbackRecentTasks(prev => prev.length === 0 ? prev : []);
        }
        return;
      }

      try {
        const [tasksData, subtasksData] = await Promise.all([
          taskAPI.getTasks({ project_ids: projectIds, limit: 50 }),
          taskAPI.getAllSubtasks({ project_ids: projectIds, limit: 50 })
        ]);

        const subtasksAsTasks = subtasksData.map((subtask: any) => {
          const parentTask = tasks.find(t => t.id === subtask.task_id);
          return {
            ...subtask,
            project_id: parentTask?.project_id || '',
            workflow_status_id: subtask.workflow_status_id,
            priority_id: subtask.priority_id,
            parent_task_id: subtask.task_id,
            reporter_id: '',
            display_number: parentTask?.display_number ?? subtask.id?.substring(0, 6),
            status: subtask.status || 'todo',
            priority: subtask.priority || 'medium',
            isSubtask: true,
          };
        });

        const allTasks = [...tasksData, ...subtasksAsTasks]
          .map(item => {
            const project = projectMap[item.project_id];
            return { ...item, project, projectKey: project?.key || '' };
          })
          .filter((task: any) => task.is_active !== false && task.is_archived !== true)
          .sort((a: any, b: any) => {
            const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
            const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
            return dateB - dateA;
          })
          .slice(0, 6);

        setFallbackRecentTasks(allTasks);
      } catch (error) {
        console.error('Failed to load fallback recent tasks:', error);
      }
    };

    loadFallbackTasks();
  // recentTasks.length (not the array ref) + stable projectIdsKey prevent cascade re-runs
  }, [recentTasks.length, isAdmin, user?.id, tasksLoading, projectsLoading, projectIdsKey]);
  const recentTasksList = useMemo(() =>
    recentTasks.length > 0 ? recentTasks : fallbackRecentTasks,
    [recentTasks, fallbackRecentTasks]
  );

  // Viewer-only status per project, derived directly from project.my_role
  // returned by the projects API. Zero extra HTTP calls — the data is already
  // in Redux from the existing fetchProjects request.
  const projectViewerStatus = useMemo(() => {
    const isAdminLike = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'super_admin';
    const map: Record<string, boolean> = {};
    if (!user?.id) return map;
    projects.forEach((p: any) => {
      if (!p?.id) return;
      const role = (p?.my_role || '').toLowerCase();
      map[p.id] = !isAdminLike && role === 'viewer';
    });
    return map;
  }, [projects, user?.id, user?.role]);
  // Fetch all tasks from all projects the user is a member of for dashboard stats
  // useEffect(() => {
  //   const fetchUserProjectTasks = async () => {
  //     // Wait for projects to finish loading before fetching tasks
  //     if (!user?.id || projectsLoading) {
  //       return;
  //     }
      
  //     try {
  //       setTaskStatsLoading(true);
  //       // Get all project IDs from projects the user is a member of
  //       const projectIds = projects
  //         .filter((p: any) => p.is_active !== false)
  //         .map((p: any) => p.id)
  //         .filter(Boolean);

  //       if (projectIds.length === 0) {
  //         setAssignedTasks([]);
  //         setTaskStatsLoading(false);
  //         return;
  //       }

  //       // Fetch tasks from all user's projects in parallel
  //       // Limit to first 20 projects to avoid too many API calls
  //       const projectIdsToFetch = projectIds.slice(0, 20);
        
  //       const taskPromises = projectIdsToFetch.map((projectId: string) =>
  //         taskAPI.getTasks({
  //           project_id: projectId,
  //           is_archived: false,
  //           limit: 500 // Get a large number per project to ensure we count all tasks
  //         }).catch((error) => {
  //           console.error(`Failed to fetch tasks for project ${projectId}:`, error);
  //           return []; // Return empty array on error to continue with other projects
  //         })
  //       );

  //       // Fetch all tasks in parallel
  //       const taskResults = await Promise.allSettled(taskPromises);

  //       // Flatten the results: each promise resolves to an array of tasks
  //       const allTasks: Task[] = [];
  //       taskResults.forEach((result) => {
  //         if (result.status === 'fulfilled' && Array.isArray(result.value)) {
  //           allTasks.push(...result.value);
  //         }
  //       });

  //       // Remove duplicates based on task ID (in case a task appears in multiple results)
  //       const uniqueTasks = Array.from(
  //         new Map(allTasks.map(task => [task.id, task])).values()
  //       );

  //       setAssignedTasks(uniqueTasks);
  //     } catch (error) {
  //       console.error('Failed to fetch tasks from user projects for dashboard stats:', error);
  //       setAssignedTasks([]);
  //     } finally {
  //       setTaskStatsLoading(false);
  //     }
  //   };

  //   fetchUserProjectTasks();
  // }, [user?.id, projects, projectsLoading]);
  useEffect(() => {
    const fetchUserProjectTasks = async () => {
      if (!user?.id || projectsLoading || !projectIds.length) {
        if (!projectsLoading && !projectIds.length) setAssignedTasks([]);
        return;
      }

      try {
        setTaskStatsLoading(true);
        const allTasks = await taskAPI.getTasks({
          project_ids: projectIds,
          is_archived: false,
          limit: 500
        });
        setAssignedTasks(allTasks);
      } catch (error) {
        console.error('Failed to fetch tasks for dashboard stats:', error);
        setAssignedTasks([]);
      } finally {
        setTaskStatsLoading(false);
      }
    };

    fetchUserProjectTasks();
  // projectIdsKey (stable string) instead of projects array ref prevents re-runs
  // on every Redux store update that doesn't change the actual project list.
  }, [user?.id, projectIdsKey, projectsLoading]);

  // Helper function to map task status to dashboard category
  const getTaskCategory = (task: Task): 'todo' | 'in_progress' | 'done' => {
    // First, try to use workflow status category if available
    if (task.workflow_status_id && workflowStatuses.length > 0) {
      const workflowStatus = workflowStatuses.find(ws => ws.id === task.workflow_status_id);
      if (workflowStatus) {
        const category = (workflowStatus.category || '').toLowerCase();
        const name = (workflowStatus.name || '').toLowerCase();
        const isFinal = workflowStatus.is_final;
        const isInitial = workflowStatus.is_initial;

        // Check for done/completed
        if (
          isFinal ||
          category === 'done' ||
          category === 'completed' ||
          name.includes('done') ||
          name.includes('completed') ||
          name.includes('complete')
        ) {
          return 'done';
        }

        // Check for in progress
        if (
          category === 'in_progress' ||
          category === 'inprogress' ||
          name.includes('progress') ||
          name.includes('development') ||
          name.includes('dev')
        ) {
          return 'in_progress';
        }

        // Check for todo/backlog
        if (
          isInitial ||
          category === 'todo' ||
          category === 'backlog' ||
          name.includes('todo') ||
          name.includes('backlog')
        ) {
          return 'todo';
        }
      }
    }

    // Fallback to status field mapping
    const status = (task.status || '').toLowerCase();

    // Check if status contains keywords for completion
    if (
      status === 'done' ||
      status === 'completed' ||
      status === 'complete' ||
      status === 'uat_completed' ||
      status === 'prod_push' ||
      status.includes('done') ||
      status.includes('completed') ||
      status.includes('complete')
    ) {
      return 'done';
    }

    // Check if status contains keywords for in progress
    if (
      status === 'in_progress' ||
      status === 'inprogress' ||
      status === 'development' ||
      status === 'dev' ||
      status.includes('progress') ||
      status.includes('development')
    ) {
      return 'in_progress';
    }

    // Default to todo for all other statuses (todo, backlog, requirements, etc.)
    return 'todo';
  };


  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!isShareOpen) return;
      if (
        shareRef.current && !shareRef.current.contains(e.target as Node) &&
        shareAnchorRef.current && !shareAnchorRef.current.contains(e.target as Node)
      ) {
        setIsShareOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsShareOpen(false); };
    const onPos = () => {
      if (!isShareOpen) return;
      const rect = shareAnchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const desired = Math.min(440, Math.max(360, window.innerWidth - 24));
      setShareWidth(desired);
      let left = Math.max(12, Math.min(rect.left, window.innerWidth - desired - 12));
      setShareLeft(Math.round(left));
      setShareTop(Math.round(rect.bottom + 8));
    };
    document.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onPos);
    window.addEventListener('scroll', onPos, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onPos);
      window.removeEventListener('scroll', onPos, true);
    };
  }, [isShareOpen]);

  const isValidEmail = (v: string) => /.+@.+\..+/.test(v.trim());

  useEffect(() => {
    if (!isShareOpen) return;
    const q = shareTo.trim();
    const fetchDefault = async () => {
      try {
        setResultsLoading(true);
        const users = await userAPI.getUsers({ is_active: true, limit: 20 });
        const mapped = (users || []).map((u: any) => ({ id: String(u.id), name: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email, email: u.email }));
        setResults(mapped); setDropdownOpen(true);
      } catch { setResults([]); } finally { setResultsLoading(false); }
    };
    const search = () => {
      const t = setTimeout(async () => {
        try {
          setResultsLoading(true);
          const res = await userAPI.searchUsers(q);
          const mapped = (res || []).map((u: any) => ({ id: String(u.id), name: u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email, email: u.email }));
          setResults(mapped); setDropdownOpen(true);
        } catch { setResults([]); } finally { setResultsLoading(false); }
      }, 250);
      return () => clearTimeout(t);
    };
    if (q.length < 2) { fetchDefault(); return; }
    const cleanup = search(); return cleanup;
  }, [shareTo, isShareOpen]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (resultsRef.current && !resultsRef.current.contains(e.target as Node)) setDropdownOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);


  const addRecipient = (rec: { id?: string; email: string; name?: string }) => {
    if (recipients.find(r => r.email.toLowerCase() === rec.email.toLowerCase())) return;
    setRecipients(prev => [...prev, rec]); setShareTo(''); setDropdownOpen(false);
  };
  const removeRecipient = (email: string) => setRecipients(prev => prev.filter(r => r.email.toLowerCase() !== email.toLowerCase()));

  const submitShare = () => {
    const pending = shareTo.trim();
    let to = recipients.map(r => r.email);
    if (isValidEmail(pending)) to = [...to, pending];
    if (to.length === 0) { toast.error('Please add at least one recipient'); return; }
    const subject = 'Shared view: Dashboard';
    const link = window.location.href;
    (async () => {
      try { await shareAPI.send({ recipients: to, subject, message: shareMsg, link }); toast.success('Share email sent'); setIsShareOpen(false); setRecipients([]); setShareTo(''); setShareMsg(''); }
      catch { toast.error('Failed to send share email'); }
    })();
  };

  // Global task stats for dashboard cards
  // Count all tasks from all projects the user is a member of (regardless of assignment)
  const activeTasks = assignedTasks.filter((task) => task.is_archived !== true);
  const todoTasks = activeTasks.filter((task) => getTaskCategory(task) === 'todo').length;
  const inProgressTasks = activeTasks.filter((task) => getTaskCategory(task) === 'in_progress').length;
  const completedTasks = activeTasks.filter((task) => getTaskCategory(task) === 'done').length;

  // Sort projects by most recent activity (based on task updates)
  const projectsWithTaskCounts = projects
    .map(project => {
      const projectTasks = tasks.filter(task => task.project_id === project.id);
      const taskCount = projectTasks.length;

      // Find the most recent task update time for this project
      let mostRecentActivity: number = 0;
      if (projectTasks.length > 0) {
        const taskTimestamps = projectTasks.map(task => {
          const updatedAt = task.updated_at ? new Date(task.updated_at).getTime() : 0;
          const createdAt = task.created_at ? new Date(task.created_at).getTime() : 0;
          return Math.max(updatedAt, createdAt);
        });
        mostRecentActivity = Math.max(...taskTimestamps);
      }

      // If no tasks, use project's updated_at or created_at as fallback
      if (mostRecentActivity === 0) {
        mostRecentActivity = project.updated_at
          ? new Date(project.updated_at).getTime()
          : (project.created_at ? new Date(project.created_at).getTime() : 0);
      }

      return {
        ...project,
        task_count: taskCount,
        mostRecentActivity
      };
    })
    .sort((a, b) => {
      // Sort by most recent activity (descending), then by project updated_at, then by created_at
      if (b.mostRecentActivity !== a.mostRecentActivity) {
        return b.mostRecentActivity - a.mostRecentActivity;
      }
      // Fallback to project updated_at
      const aUpdated = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const bUpdated = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      if (bUpdated !== aUpdated) {
        return bUpdated - aUpdated;
      }
      // Final fallback to created_at
      const aCreated = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bCreated = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bCreated - aCreated;
    });
  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
  };

  // Load comments for a task and format activity
  const loadComments = async (taskId: string) => {
    try {
      const c: ProjectComment[] = await commentAPI.getComments(taskId);
      setComments(c);
      setActivity(formatCommentsForActivity(c));
    } catch (err) {
      console.error('loadComments failed', err);
      setComments([]);
      setActivity([]);
    }
  };

  const openSubtaskModal = (subtask: Subtask, parentTask: any) => {
    setParentTaskForSubtask(parentTask);
    setActiveSubtask(subtask);
    setIsTaskModalOpen(false);
    setIsSubtaskModalOpen(true);
  };

  const closeSubtaskModal = () => {
    setIsSubtaskModalOpen(false);
    setActiveSubtask(null);
    setParentTaskForSubtask(null);
  };

  // Helper function to refresh project members
  const refreshProjectMembers = async (projectId?: string) => {
    const targetProjectId = projectId || currentProject?.id;
    if (!targetProjectId) {
      return;
    }
    try {
      const data = await projectMemberAPI.getProjectMembers(targetProjectId);
      setProjectMembers(data || []);
    } catch (error) {
      console.error('Failed to fetch project members:', error);
    }
  };

  const isSubtaskEntity = (item: any): item is Subtask => {
    return Boolean(item && item.task_id);
  };

  // Handle task click - fetch full task from API and open modal
  const handleTaskClick = async (task: any) => {
    const subtaskClicked = isSubtaskEntity(task);

    try {
      if (subtaskClicked) {
        const parentId = task.task_id;
        const [parentTask, parentSubtasks] = await Promise.all([
          taskAPI.getTask(parentId),
          taskAPI.getSubtasks(parentId)
        ]);

        const resolvedProjectId = parentTask?.project_id || (task as any)?.project_id || currentProject?.id;
        if (resolvedProjectId) {
          await refreshProjectMembers(resolvedProjectId);
        }

        const matchedSubtask = parentSubtasks.find((s) => String(s.id) === String(task.id));
        openSubtaskModal(
          (matchedSubtask as Subtask) || (task as Subtask),
          parentTask || (task as any)
        );
        return;
      }

      // Always refresh project members to ensure we have the latest list
      if (task.project_id || currentProject?.id) {
        await refreshProjectMembers(task.project_id || currentProject?.id);
      }

      // Fetch full task directly from API for reliable data
      const full = await taskAPI.getTask(task.id);
      setSelectedTask(full || task);

      // Load comments & activity
      await loadComments(task.id);

      setIsTaskModalOpen(true);
    } catch (err) {
      console.error('failed to load task details for modal, falling back to basic task', err);
      if (subtaskClicked) {
        setParentTaskForSubtask((prev: any) => prev || (task as any));
        setActiveSubtask(task as Subtask);
        setIsTaskModalOpen(false);
        setIsSubtaskModalOpen(true);
        return;
      }
      // Fallback behavior: still open modal with basic task and loaded comments
      setSelectedTask(task);
      await loadComments(task.id);
      setIsTaskModalOpen(true);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (taskId: string, workflowStatusId: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { workflow_status_id: workflowStatusId || undefined }
      })).unwrap();

      // Refresh the selected task to reflect the status change
      if (selectedTask?.id === taskId) {
        try {
          const fresh = await taskAPI.getTask(taskId);
          setSelectedTask(fresh);
        } catch { /* ignore */ }
      }

      // Refresh recent tasks
      // dispatch(fetchRecentTasks({ limit: 6 }));
      const projectIds = projects
        .filter((p: any) => p.is_active !== false)
        .map((p: any) => p.id)
        .filter(Boolean);
        dispatch(fetchRecentTasks({ limit: 6, project_ids: projectIds }));
    } catch (e) {
      console.error('status update failed', e);
    }
  };

  const handleStartEdit = (project: Project) => {
    setEditingProject(project);
    setEditName(project.name);
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setEditName('');
  };

  const handleSaveEdit = async () => {
    if (!editingProject || !editName.trim() || editName.trim().length < 3) return;
    setIsUpdating(true);
    try {
      await dispatch(updateProject({ 
        id: editingProject.id, 
        data: { name: editName.trim() } 
      })).unwrap();
      setEditingProject(null);
      setEditName('');
      dispatch(fetchProjects({ is_active: true, organization_id: user?.organization_id }));
    } catch { } finally { setIsUpdating(false); }
  };

  const handleDeleteClick = (project: Project) => {
    setShowDeleteConfirm(project);
  };

  const handleConfirmDelete = async () => {
    if (!showDeleteConfirm) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteProject(showDeleteConfirm.id)).unwrap();
      dispatch(fetchProjects({ is_active: true, organization_id: user?.organization_id }));
    } catch { } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back, {user?.name}!</p>
        </div>
        <div className="relative"><Share label="Share" className="btn btn-secondary flex items-center gap-2" subject="Shared view: Dashboard" /></div>
      </div>
      {/* Shared Share component handles popover */}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Projects</p>
              <p className="text-2xl font-bold text-gray-900">{projects.length}</p>
            </div>
            <BarChart3 className="w-10 h-10 text-primary-600" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">To Do</p>
              <p className="text-2xl font-bold text-gray-900">{todoTasks}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-yellow-600" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">{inProgressTasks}</p>
            </div>
            <Clock className="w-10 h-10 text-blue-600" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{completedTasks}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
        </div>
      </div>

      {/* Recent Projects */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Recent Projects</h2>
          <button
            onClick={() => navigate('/projects')}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            View all →
          </button>
        </div>

        {projectsLoading ? (
          <div className="text-center py-8">Loading projects...</div>
        ) : projects.length === 0 ? (
          <div className="card text-center py-8 text-gray-500">
            No projects yet. Create your first project!
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projectsWithTaskCounts.slice(0, 3).map((project) => (
              <div key={project.id} className="relative">
                {editingProject?.id === project.id ? (
                  <div className="card">
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit();
                          else if (e.key === 'Escape') handleCancelEdit();
                        }}
                        className="flex-1 text-lg font-semibold px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isUpdating}
                        autoFocus
                        maxLength={200}
                      />
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={handleSaveEdit}
                          disabled={isUpdating || !editName.trim() || editName.trim().length < 3}
                          className="p-1.5 hover:bg-green-100 rounded text-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Save"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={isUpdating}
                          className="p-1.5 hover:bg-red-100 rounded text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      <span className="badge badge-primary">{project.key}</span>
                    </div>
                  </div>
                ) : (
                  <ProjectCard project={project} onEdit={handleStartEdit} onDelete={handleDeleteClick} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Tasks */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Recent Tasks</h2>
          <button
            onClick={() => navigate('/tasks')}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            View all →
          </button>
        </div>

        {tasksLoading ? (
          <Loader variant="overlay" size="xl" text="Loading tasks..." />
        ) : recentTasksList.length === 0 ? (
          <div className="card text-center py-8 text-gray-500">
            No recent tasks yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentTasksList.slice(0, 6).map((task) => {
              const taskProjectId = (task as any).project_id;
              // Determine if user is viewer-only for this specific task's project
              const taskIsViewerOnly = taskProjectId ? (projectViewerStatus[taskProjectId] ?? false) : false;
              
              return (
                <TaskCard
                  key={task.id || task.task_id}
                  task={task}
                  priorities={priorities}
                  project={
                    (projects.find(p => p.id === task.project_id)) ||
                    ((task as any).project_id ? projectMap[(task as any).project_id as string] : undefined)
                  }
                  onClick={() => handleTaskClick(task)}
                  onDelete={() => {
                    dispatch(fetchRecentTasks({ limit: 6, project_ids: projectIds }));
                  }}
                  isViewerOnly={taskIsViewerOnly}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Task Modal */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setSelectedTask(null);
          setActivity([]);
          setComments([]);
        }}
        task={selectedTask}
        onStatusUpdate={async (taskId, newStatus) => {
          await handleStatusUpdate(taskId, newStatus);
          try {
            const fresh = await taskAPI.getTask(taskId);
            setSelectedTask(fresh);
          } catch { /* ignore */ }
        }}
        onAddComment={async (taskId, comment, parentId) => {
          const createdComment = await commentAPI.createComment({ task_id: taskId, content: comment, parent_comment_id: parentId });
          await loadComments(taskId);
          return createdComment;
        }}
        onUpdateDescription={async (taskId, description) => {
          await dispatch(updateTask({ id: taskId, data: { description } }));
          try {
            const fresh = await taskAPI.getTask(taskId);
            setSelectedTask(fresh);
          } catch { /* ignore */ }
        }}
        onUpdateAssignee={async (taskId, assigneeId) => {
          await dispatch(updateTask({ id: taskId, data: { assigned_to: assigneeId === null ? null : assigneeId } }));
          try {
            const fresh = await taskAPI.getTask(taskId);
            setSelectedTask(fresh);
          } catch { /* ignore */ }
          // Refresh project members after assignee update
          if (selectedTask?.project_id || currentProject?.id) {
            await refreshProjectMembers(selectedTask?.project_id || currentProject?.id);
          }
        }}
        onUpdateReporter={async (taskId, reporterId) => {
          await dispatch(updateTask({ id: taskId, data: { reporter_id: reporterId === null ? null : reporterId } }));
          try {
            const fresh = await taskAPI.getTask(taskId);
            setSelectedTask(fresh);
          } catch { /* ignore */ }
          // Refresh project members after reporter update
          if (selectedTask?.project_id || currentProject?.id) {
            await refreshProjectMembers(selectedTask?.project_id || currentProject?.id);
          }
        }}
        activity={activity}
        taskDetails={selectedTask ? (() => {
          const rawLabels: any = (selectedTask as any)?.labels;
          const labels: string[] = Array.isArray(rawLabels)
            ? rawLabels
            : (typeof rawLabels === 'string'
              ? rawLabels.split(',').map((l: string) => l.trim()).filter(Boolean)
              : []);
          return {
            assignee: selectedTask.assigned_to || 'Unassigned',
            labels,
            sprint: selectedTask.sprint_id || 'Not set',
            dueDate: selectedTask.due_date || 'Not set'
          };
        })() : {}}
        projectMembers={projectMembers}
        comments={comments}
        onDeleteComment={async (commentId) => {
          await deleteComment(commentId);
          if (selectedTask) await loadComments(selectedTask.id);
        }}
        onUpdateComment={async (commentId, content) => {
          await updateComment(commentId, content);
          if (selectedTask) await loadComments(selectedTask.id);
        }}
        projectKey={currentProject?.key || projects.find(p => p.id === selectedTask?.project_id)?.key || 'AHM'}
        project={
          (() => {
            if (!selectedTask?.project_id) return undefined;
            return projectMap[selectedTask.project_id];
          })()
        }
        onUpdateTaskTitle={async (id, title) => {
          await dispatch(updateTask({ id, data: { title } }));
          try {
            const fresh = await taskAPI.getTask(id);
            setSelectedTask(fresh);
          } catch { /* ignore */ }
        }}
        onUpdateFields={async (id, updates) => {
          // Don't call updateTask again - it's already been called by the component
          // Just refresh the task data
          try {
            const fresh = await taskAPI.getTask(id);
            setSelectedTask(fresh);
          } catch { /* ignore */ }
          // Refresh project members if assignee was updated
          if (updates.assigned_to !== undefined && (selectedTask?.project_id || currentProject?.id)) {
            await refreshProjectMembers(selectedTask?.project_id || currentProject?.id);
          }
        }}
        onOpenSubtask={(subtask, parent) => {
          if (subtask) {
            openSubtaskModal(subtask, parent || selectedTask);
          }
        }}
        // Viewer can only see, not mutate
        // Check per-task project viewer status
        isViewerOnly={
          (() => {
            const taskProjectId = selectedTask?.project_id;
            if (!taskProjectId) return false;
            return projectViewerStatus[taskProjectId] ?? false;
          })()
        }
      />

      {activeSubtask && (
        <SubtaskModal
          subtask={activeSubtask}
          isOpen={isSubtaskModalOpen}
          onClose={closeSubtaskModal}
          parentKey={
            (() => {
              const source = parentTaskForSubtask || selectedTask;
              if (!source?.project_id) return currentProject?.key;
              const project = projects.find(p => p.id === source.project_id) || currentProject;
              const projectKey = project?.key || 'AHM';
              const displayNumber = (source as any)?.display_number;
              return displayNumber ? `${projectKey}-${displayNumber}` : projectKey;
            })()
          }
          parentTitle={(parentTaskForSubtask || selectedTask)?.title}
          parentTask={(parentTaskForSubtask || selectedTask) as any}
          onOpenParent={(parent) => {
            closeSubtaskModal();
            if (parent) {
              handleTaskClick(parent);
            }
          }}
          projectMembers={projectMembers}
          activity={activity}
          projectKey={
            (() => {
              const source = parentTaskForSubtask || selectedTask;
              if (!source?.project_id) return currentProject?.key;
              const project = projects.find(p => p.id === source.project_id) || currentProject;
              return project?.key;
            })()
          }
          project={
            (() => {
              const task = parentTaskForSubtask || selectedTask;
              if (!task?.project_id) return undefined;
              return projectMap[task.project_id];
            })()
          }
          onAddComment={async (taskId, html, parentId, plainText) => {
            const contentHtml = html;
            const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
            const created = await commentAPI.createComment({
              task_id: taskId,
              content: contentText,
              content_html: contentHtml,
              content_text: contentText,
              parent_comment_id: parentId
            });
            if (parentTaskForSubtask || selectedTask) {
              await loadComments((parentTaskForSubtask || selectedTask)?.id || '');
            }
            return created;
          }}
          onUpdateComment={async (commentId, html, plainText) => {
            await updateComment(commentId, html, plainText);
            if (parentTaskForSubtask || selectedTask) {
              await loadComments((parentTaskForSubtask || selectedTask)?.id || '');
            }
          }}
          onDeleteComment={async (commentId) => {
            await deleteComment(commentId);
            if (parentTaskForSubtask || selectedTask) {
              await loadComments((parentTaskForSubtask || selectedTask)?.id || '');
            }
          }}
          onSave={async (updates) => {
            if (!activeSubtask) return;
            await dispatch(updateSubtask({ subtaskId: activeSubtask.id, data: updates })).unwrap();
            try {
              const fresh = await subtaskAPI.get(activeSubtask.id);
              setActiveSubtask(fresh);
            } catch (error) {
              console.error('Failed to refresh subtask:', error);
            }
          }}
          onDelete={async () => {
            setIsSubtaskModalOpen(false);
          }}
          // Viewer can only see, not mutate
          // Check per-task project viewer status based on parent task
          isViewerOnly={
            (() => {
              const task = parentTaskForSubtask || selectedTask;
              const taskProjectId = task?.project_id;
              if (!taskProjectId) return false;
              return projectViewerStatus[taskProjectId] ?? false;
            })()
          }
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <DeleteConfirmDialog
          isOpen={!!showDeleteConfirm}
          itemName={showDeleteConfirm.name}
          itemType="project"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteConfirm(null)}
          isLoading={isDeleting}
          customMessage="Are you sure you want to delete this project? This will hide the project and all its tasks, stories, epics, and issues from the system. This action cannot be undone."
        />
      )}
    </div>
  );
};
